import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ReadOnlyField } from '../FormRow';
import { Plus, Trash2 } from 'lucide-react';

export const FinancialsTab = ({
  load,
  setLoad,
  totals,
  fscAutoInfo,
  fscAutoLoading,
  fscManualOverride,
  setFscManualOverride,
  localAccessorials,
  addLocalAccessorial,
  updateLocalAccessorial,
  removeLocalAccessorial,
  getFieldClass,
  changedFields,
  showCommission,
  canEditAMOP,
  staffUsers,
}) => {
  const amUsers = useMemo(() => staffUsers.filter(u => ['account_manager', 'super_admin', 'admin'].includes(u.role)), [staffUsers]);
  const opUsers = useMemo(() => staffUsers.filter(u => ['operator', 'account_manager'].includes(u.role)), [staffUsers]);
  return (
    <>
            {/* Margin Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-3 text-center">
                <p className="text-xs text-[var(--tms-text-muted)]">Customer Total</p>
                <p className="text-lg font-bold font-mono">${totals.customerTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-3 text-center">
                <p className="text-xs text-[var(--tms-text-muted)]">Carrier Total</p>
                <p className="text-lg font-bold font-mono">${totals.carrierTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className={`rounded-lg p-3 text-center border ${totals.margin >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                <p className="text-xs text-[var(--tms-text-muted)]">Margin ($)</p>
                <p className={`text-lg font-bold font-mono ${totals.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totals.margin >= 0 ? '+' : ''}${totals.margin.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className={`rounded-lg p-3 text-center border ${totals.marginPct >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                <p className="text-xs text-[var(--tms-text-muted)]">Margin (%)</p>
                <p className={`text-lg font-bold font-mono ${totals.marginPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totals.marginPct >= 0 ? '+' : ''}{totals.marginPct.toFixed(1)}%
                </p>
              </div>
            </div>

            {/* Mileage & Rate Type Section */}
            <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-3 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs text-[var(--tms-text-muted)]">Rate Type</Label>
                  <Select value={load.line_haul_type || 'flat'} onValueChange={(val) => {
                    const updates = { line_haul_type: val };
                    if (val === 'rpm' && load.customer_mileage && load.customer_rpm) {
                      updates.customer_line_haul = Math.round(load.customer_mileage * load.customer_rpm * 100) / 100;
                    }
                    if (val === 'rpm' && load.customer_mileage && load.carrier_rpm) {
                      updates.carrier_line_haul = Math.round(load.customer_mileage * load.carrier_rpm * 100) / 100;
                    }
                    setLoad(prev => ({ ...prev, ...updates }));
                  }}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-rate-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat Rate</SelectItem>
                      <SelectItem value="rpm">RPM (Rate Per Mile)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[var(--tms-text-muted)]">Customer Mileage</Label>
                  <Input type="number" step="0.1" value={load.customer_mileage || ''} onChange={(e) => {
                    const mi = parseFloat(e.target.value) || 0;
                    const updates = { customer_mileage: mi };
                    if (load.line_haul_type === 'rpm') {
                      if (load.customer_rpm) updates.customer_line_haul = Math.round(mi * load.customer_rpm * 100) / 100;
                      if (load.carrier_rpm) updates.carrier_line_haul = Math.round(mi * load.carrier_rpm * 100) / 100;
                    }
                    if (load.fsc_enabled && load.fsc_rpm) {
                      updates.fsc_amount = Math.round(mi * load.fsc_rpm * 100) / 100;
                    }
                    setLoad(prev => ({ ...prev, ...updates }));
                  }} className="h-8 text-sm font-mono" placeholder="Miles" data-testid="input-customer-mileage" />
                </div>
                <div>
                  <Label className="text-xs text-[var(--tms-text-muted)]">Load Mileage</Label>
                  <Input type="number" step="0.1" value={load.load_mileage || ''} onChange={(e) => setLoad({ ...load, load_mileage: parseFloat(e.target.value) || null })} className="h-8 text-sm font-mono" placeholder="Auto/Manual" data-testid="input-load-mileage" />
                </div>
                {load.line_haul_type === 'rpm' && (
                  <>
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Customer RPM</Label>
                      <Input type="number" step="0.01" value={load.customer_rpm || ''} onChange={(e) => {
                        const rpm = parseFloat(e.target.value) || 0;
                        const updates = { customer_rpm: rpm };
                        if (load.customer_mileage) updates.customer_line_haul = Math.round(load.customer_mileage * rpm * 100) / 100;
                        setLoad(prev => ({ ...prev, ...updates }));
                      }} className="h-8 text-sm font-mono" placeholder="$/mi" data-testid="input-customer-rpm" />
                    </div>
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Carrier RPM</Label>
                      <Input type="number" step="0.01" value={load.carrier_rpm || ''} onChange={(e) => {
                        const rpm = parseFloat(e.target.value) || 0;
                        const updates = { carrier_rpm: rpm };
                        if (load.customer_mileage) updates.carrier_line_haul = Math.round(load.customer_mileage * rpm * 100) / 100;
                        setLoad(prev => ({ ...prev, ...updates }));
                      }} className="h-8 text-sm font-mono" placeholder="$/mi" data-testid="input-carrier-rpm" />
                    </div>
                  </>
                )}
              </div>

              {/* Fuel Surcharge controls (moved here from below the rates table) */}
              <div className="mt-3 pt-3 border-t border-[var(--tms-border)] flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="fsc_enabled" 
                    checked={load.fsc_enabled || false} 
                    onCheckedChange={(checked) => setLoad({ ...load, fsc_enabled: checked, fsc_amount: checked ? load.fsc_amount : null })}
                    data-testid="checkbox-fsc-enabled"
                  />
                  <Label htmlFor="fsc_enabled" className="text-xs cursor-pointer font-medium">Fuel Surcharge</Label>
                </div>
                {load.fsc_enabled && (
                  <>
                    <div className="flex items-center gap-1">
                      <Label className="text-xs text-[var(--tms-text-muted)]">FSC %</Label>
                      <Input type="number" step="0.1" value={load.fsc_percentage || ''} onChange={(e) => {
                        const pct = parseFloat(e.target.value) || 0;
                        const amt = load.customer_line_haul ? Math.round(load.customer_line_haul * pct / 100 * 100) / 100 : 0;
                        setFscManualOverride(true);
                        setLoad({ ...load, fsc_percentage: pct, fsc_amount: amt, fsc_rpm: null });
                      }} className="w-16 h-7 text-xs font-mono text-right" placeholder="%" data-testid="input-fsc-pct" />
                    </div>
                    <div className="flex items-center gap-1">
                      <Label className="text-xs text-[var(--tms-text-muted)]">FSC RPM</Label>
                      <Input type="number" step="0.01" value={load.fsc_rpm || ''} onChange={(e) => {
                        const rpm = parseFloat(e.target.value) || 0;
                        const amt = load.customer_mileage ? Math.round(load.customer_mileage * rpm * 100) / 100 : 0;
                        setFscManualOverride(true);
                        setLoad({ ...load, fsc_rpm: rpm, fsc_amount: amt, fsc_percentage: null });
                      }} className="w-20 h-7 text-xs font-mono text-right" placeholder="$/mi" data-testid="input-fsc-rpm" />
                    </div>
                    {fscAutoInfo && !fscManualOverride && (
                      <span className="text-[10px] text-amber-700" data-testid="fsc-auto-info-inline">
                        Auto from EIA ${fscAutoInfo.fuel_price?.toFixed(3)} (week of {fscAutoInfo.price_date}, {fscAutoInfo.week_preference})
                      </span>
                    )}
                    {fscManualOverride && (
                      <>
                        <span className="text-[10px] text-blue-700">Manual override</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setFscManualOverride(false)}
                          data-testid="reset-fsc-auto-button"
                        >
                          Reset to Auto
                        </Button>
                      </>
                    )}
                    {fscAutoLoading && (
                      <span className="text-[10px] text-[var(--tms-text-muted)]">Looking up FSC...</span>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Rates Table */}
            <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tms-hover)]">
                  <tr>
                    <th className="text-left p-3 font-semibold text-xs">Item</th>
                    <th className="text-right p-3 font-semibold text-xs">Carrier</th>
                    <th className="text-right p-3 font-semibold text-xs">Customer</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Line Haul Row */}
                  <tr className="border-t border-[var(--tms-border)]">
                    <td className="p-3 font-medium">
                      Line Haul
                      {load.line_haul_type === 'rpm' && <span className="text-xs text-[var(--tms-text-muted)] ml-1">(RPM)</span>}
                    </td>
                    <td className="p-2">
                      <Input type="number" step="0.01" value={load.carrier_line_haul || 0} onChange={(e) => setLoad({ ...load, carrier_line_haul: parseFloat(e.target.value) || 0 })} className={`h-8 text-right font-mono text-sm ${getFieldClass('carrier_line_haul')}`} data-testid="input-carrier-line-haul" />
                    </td>
                    <td className="p-2">
                      <Input type="number" step="0.01" value={load.customer_line_haul || 0} onChange={(e) => setLoad({ ...load, customer_line_haul: parseFloat(e.target.value) || 0 })} className={`h-8 text-right font-mono text-sm ${getFieldClass('customer_line_haul')}`} data-testid="input-customer-line-haul" />
                    </td>
                    <td></td>
                  </tr>
                  
                  {/* FSC Row - customer-side only when enabled */}
                  {load.fsc_enabled && (
                    <tr className="border-t border-[var(--tms-border)] bg-amber-50/50">
                      <td className="p-3 font-medium text-amber-700">
                        Fuel Surcharge
                        {load.fsc_percentage ? <span className="text-xs ml-1">({load.fsc_percentage}%)</span> : null}
                        {load.fsc_rpm ? <span className="text-xs ml-1">(${load.fsc_rpm}/mi)</span> : null}
                      </td>
                      <td className="p-2 text-right text-xs text-[var(--tms-text-muted)] italic">—</td>
                      <td className="p-2">
                        <Input type="number" step="0.01" value={load.fsc_amount || ''} readOnly className="h-8 text-right font-mono text-sm bg-amber-50" data-testid="input-fsc-amount-customer" />
                      </td>
                      <td></td>
                    </tr>
                  )}

                  {/* Accessorial Rows */}
                  {localAccessorials.map((acc, index) => (
                    <tr key={index} className="border-t border-[var(--tms-border)]">
                      <td className="p-2">
                        <Input value={acc.name} onChange={(e) => updateLocalAccessorial(index, 'name', e.target.value)} placeholder="Name" className="h-8 text-sm uppercase" style={changedFields.local_accessorials ? { backgroundColor: '#FEF9C3' } : {}} data-testid={`input-acc-name-${index}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.01" value={acc.carrier_amount || ''} onChange={(e) => updateLocalAccessorial(index, 'carrier_amount', e.target.value)} className="h-8 text-right font-mono text-sm" style={changedFields.local_accessorials ? { backgroundColor: '#FEF9C3' } : {}} data-testid={`input-acc-carrier-${index}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.01" value={acc.customer_amount || ''} onChange={(e) => updateLocalAccessorial(index, 'customer_amount', e.target.value)} className="h-8 text-right font-mono text-sm" style={changedFields.local_accessorials ? { backgroundColor: '#FEF9C3' } : {}} data-testid={`input-acc-customer-${index}`} />
                      </td>
                      <td className="p-2 text-center">
                        <button onClick={() => removeLocalAccessorial(index)} className="text-red-500 hover:text-red-700" data-testid={`remove-acc-${index}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-[var(--tms-hover)]">
                  <tr className="border-t-2 border-[var(--tms-border)]">
                    <td className="p-3 font-bold text-sm">TOTALS</td>
                    <td className="p-3 text-right font-mono font-bold">${totals.carrierTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="p-3 text-right font-mono font-bold">${totals.customerTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Fuel Surcharge + Add Accessorial */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" onClick={addLocalAccessorial} data-testid="add-accessorial-button">
                <Plus className="w-4 h-4 mr-1" /> Add Accessorial
              </Button>
            </div>

            {/* Commission Assignments */}
            {showCommission && (
              <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4 mt-4">
                <h3 className="text-sm font-semibold text-[var(--tms-text)] mb-3">COMMISSION ASSIGNMENTS</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* AM */}
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Account Manager</Label>
                      {canEditAMOP ? (
                        <Select value={load.account_manager_id || "none"} onValueChange={(val) => { const u = staffUsers.find(s => s.id === val); setLoad({ ...load, account_manager_id: val === "none" ? "" : val, account_manager_name: u?.name || "" }); }}>
                          <SelectTrigger className="h-8 text-sm" data-testid="select-account-manager"><SelectValue placeholder="Select AM..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- None --</SelectItem>
                            {amUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : <ReadOnlyField value={load.account_manager_name} placeholder="Not assigned" />}
                    </div>
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Commission %</Label>
                      {canEditAMOP ? (
                        <Input type="number" step="0.1" min="0" max="100" value={load.account_manager_commission_pct || ''} onChange={(e) => setLoad({ ...load, account_manager_commission_pct: parseFloat(e.target.value) || 0 })} className="h-8 text-sm text-right font-mono" data-testid="input-am-commission" />
                      ) : <ReadOnlyField value={load.account_manager_commission_pct ? `${load.account_manager_commission_pct}%` : null} />}
                    </div>
                  </div>
                  {/* OP1 */}
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Operator 1</Label>
                      {canEditAMOP ? (
                        <Select value={load.operator_id || "none"} onValueChange={(val) => { const u = staffUsers.find(s => s.id === val); setLoad({ ...load, operator_id: val === "none" ? "" : val, operator_name: u?.name || "" }); }}>
                          <SelectTrigger className="h-8 text-sm" data-testid="select-operator1"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- None --</SelectItem>
                            {opUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : <ReadOnlyField value={load.operator_name} placeholder="Not assigned" />}
                    </div>
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Commission %</Label>
                      {canEditAMOP ? (
                        <Input type="number" step="0.1" min="0" max="100" value={load.operator_commission_pct || ''} onChange={(e) => setLoad({ ...load, operator_commission_pct: parseFloat(e.target.value) || 0 })} className="h-8 text-sm text-right font-mono" data-testid="input-op1-commission" />
                      ) : <ReadOnlyField value={load.operator_commission_pct ? `${load.operator_commission_pct}%` : null} />}
                    </div>
                  </div>
                  {/* OP2 */}
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Operator 2</Label>
                      {canEditAMOP ? (
                        <Select value={load.operator2_id || "none"} onValueChange={(val) => { const u = staffUsers.find(s => s.id === val); setLoad({ ...load, operator2_id: val === "none" ? "" : val, operator2_name: u?.name || "" }); }}>
                          <SelectTrigger className="h-8 text-sm" data-testid="select-operator2"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- None --</SelectItem>
                            {opUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : <ReadOnlyField value={load.operator2_name} placeholder="Not assigned" />}
                    </div>
                    <div>
                      <Label className="text-xs text-[var(--tms-text-muted)]">Commission %</Label>
                      {canEditAMOP ? (
                        <Input type="number" step="0.1" min="0" max="100" value={load.operator2_commission_pct || ''} onChange={(e) => setLoad({ ...load, operator2_commission_pct: parseFloat(e.target.value) || 0 })} className="h-8 text-sm text-right font-mono" data-testid="input-op2-commission" />
                      ) : <ReadOnlyField value={load.operator2_commission_pct ? `${load.operator2_commission_pct}%` : null} />}
                    </div>
                  </div>
                </div>
                {!canEditAMOP && <p className="text-xs text-indigo-600 mt-3">Only Super Admin and Admin can modify commission assignments.</p>}
              </div>
            )}
    </>
  );
};

export default FinancialsTab;
